import { pageConfig } from "@/common/service/page-config";
import { planTypes } from '@/common/constants';
import {
  PRO_PLUS_SOFT_BUNDLE_NAME_LIST,
  BIZ_PLUS_SOFT_BUNDLE_NAME_LIST,
  PBX_SOFT_BUNDLE_NAME_LIST,
  SOFT_BUNDLE_NAME_INFO,
  SOFT_BUNDLE_PLAN_GROUP,
} from './constant';
import _ from 'lodash';
import { computedCallingPlans } from '@/common/service/computers/plans/pbx';
import { PBX_REGIONS } from '@/plans/ZOOM_PHONE/helper/constants';

export const getSoftBundleConfiguration = () => {
  const { features } = pageConfig?.partnerReferral || {};
  return features?.softBundleConfig;
};

export const getSoftBundlePlanGroup = (planType) => {
  const softBundleConfiguration = getSoftBundleConfiguration();
  // when config showPlanGroupHeader is false, return undefined
  if (!softBundleConfiguration?.config?.showPlanGroupHeader) return undefined;
  const configPlanTypeKeys = _.keys(softBundleConfiguration);
  return _.includes(configPlanTypeKeys, planType) ? SOFT_BUNDLE_PLAN_GROUP : undefined;
};

export const getCurrentSoftBundleName = () => {
  return pageConfig?.partnerReferral?.features?.softBundleConfig?.config?.bundleName;
}

// for soft bundle with pro + pbx, the plan type context is the plan type of the pro plus plan
export const getCurrentSoftBundlePlanTypeContext = () => {
  return pageConfig?.partnerReferral?.features?.softBundleConfig?.config?.planTypeContext;
}

// from soft bundle with pro + pbx, softBundleConfig field from jointsale op configuration
export const isSoftProPlusBundle = () => {
  const currentBundleName = getCurrentSoftBundleName();
  const currentPlanTypeContext = getCurrentSoftBundlePlanTypeContext();

  return PRO_PLUS_SOFT_BUNDLE_NAME_LIST.includes(currentBundleName)
  || currentPlanTypeContext === planTypes.ZONE_PRO_PLUS;
}

export const isSoftBundleBizPlus = () => {
  const currentBundleName = getCurrentSoftBundleName();
  const currentPlanTypeContext = getCurrentSoftBundlePlanTypeContext();

  return BIZ_PLUS_SOFT_BUNDLE_NAME_LIST.includes(currentBundleName)
  || currentPlanTypeContext === planTypes.ZOOM_ONE_BUNDLE;
}

// pbx + scheduler + ....
export const isSimilarPbxBundle = () => {
  const currentBundleName = getCurrentSoftBundleName();
  const currentPlanTypeContext = getCurrentSoftBundlePlanTypeContext();
  return PBX_SOFT_BUNDLE_NAME_LIST.includes(currentBundleName)
  || currentPlanTypeContext === planTypes.ZOOM_PHONE;
}

// contain pro plus(only for us/ca) and soft bundle pro plus(pro + zoom phone)
export const isSimilarProPlusBundle = () => {
  return isSoftProPlusBundle() || pageConfig?.editConfig?.planType === planTypes.ZONE_PRO_PLUS;
}

export const isSimilarBizPlusBundle = () => {
  return isSoftBundleBizPlus() || pageConfig?.editConfig?.planType === planTypes.ZOOM_ONE_BUNDLE;
}

export const getSoftBundleMessages = (bundleName) => {
  const info = {
    [SOFT_BUNDLE_NAME_INFO.clinical]: {
      name: 'softBundle.clinical.name',
      title: 'softBundle.clinical.title',
      description: 'softBundle.clinical.description',
      tips: 'softBundle.clinical.tips',
    },
    [SOFT_BUNDLE_NAME_INFO.brick_mortar]: {
      name: 'softBundle.brick_mortar.name',
      title: 'softBundle.brick_mortar.title',
      description: 'softBundle.brick_mortar.description',
      tips: 'softBundle.brick_mortar.tips',
    },
    [SOFT_BUNDLE_NAME_INFO.professional]: {
      name: 'softBundle.professional.name',
      title: 'softBundle.professional.title',
      description: 'softBundle.professional.description',
      tips: 'softBundle.professional.tips',
    },
  }

  return info[bundleName] ?? {};
}

export const softBundleUtils = {
  get bundleName() {
    return getCurrentSoftBundleName();
  },
  isClinical() {
    return this.bundleName === SOFT_BUNDLE_NAME_INFO.clinical;
  },
  isBrickMortar() {
    return this.bundleName === SOFT_BUNDLE_NAME_INFO.brick_mortar;
  },
  isProfessional() {
    return this.bundleName === SOFT_BUNDLE_NAME_INFO.professional;
  },
  isSimilarPbxBundle,
  isSimilarProPlusBundle,
  isSimilarBizPlusBundle,
  isSoftProPlusBundle,
  isSoftBundleBizPlus,
  hasCustomSorts() {
    return !_.isEmpty(getSoftBundleSorts());
  },
  getCustomSorts() {
    return getSoftBundleSorts();
  }
}

export const getSoftBundleSorts = () => {
  const softBundleConfig = getSoftBundleConfiguration();
  const sortList = softBundleConfig?.config?.sortList;
  if (!sortList) return null;

  // support sort list
  if (Array.isArray(sortList)) {
    return sortList;
  }

  return null;
};

export const forCurrentPlanShowInAddonForSoftBundle = (planType) => {
  const softBundleConfiguration = getSoftBundleConfiguration();
  return _.get(softBundleConfiguration, [planType, 'showInAddon'], false);
}

export const softBundleForAddon = {
  [planTypes.ZOOM_PHONE$ZPI]: {
    test: () => {
      return isSimilarBizPlusBundle() || isSimilarProPlusBundle() || isSimilarPbxBundle();
    }
  },
  [planTypes.ZOOM_PHONE$ADDITIONAL]: {
    test: (store) => {
      const bundlePlanList = _.values(store.getters.softBundleState?.bundlePlanMap);
      return _.some(computedCallingPlans(bundlePlanList), (p) => p.region === PBX_REGIONS.US_AND_CANADA);
    }
  }
}

/**
 * Sanitize planType to create a valid CSS selector
 * Replace special characters that are not valid in CSS selectors
 * CSS ID selectors can contain: letters, numbers, hyphens, underscores
 * CSS ID cannot start with a digit
 *
 * @param {string|number|undefined|null} planType - The plan type to sanitize
 * @returns {string} Sanitized plan type that is a valid CSS ID selector
 */
function sanitizePlanTypeForSelector(planType) {
  if (!planType) return '';
  // Ensure the returned string is a valid CSS ID selector
  // CSS ID cannot start with a digit
  const sanitized = String(planType).replace(/[^a-zA-Z0-9_-]/g, '-');
  // If it starts with a digit, add a prefix
  if (/^\d/.test(sanitized)) {
    return `plan-${sanitized}`;
  }
  return sanitized;
}

export const softBundleUiUtils = {
  getPlanQuantityTeleportInfo(needTeleport, planType) {
    const sanitizedPlanType = sanitizePlanTypeForSelector(planType);
    const id = `config-plan-teleport-target-${sanitizedPlanType}`;
    return needTeleport ? {
      id,
      target: `#${id}`,
    } : {
      id: '',
      target: '',
    }
  },
}

export const isInSoftBundleContext = () => {
  const bundleConfig = getSoftBundleConfiguration()
  return !_.isEmpty(bundleConfig);
}
